import { type AnimationClip } from 'three'
import { TrackBoneRenamer } from './TrackBoneRenamer'
import { QuaterniusMapper } from '../../retarget/bone-automap/QuaterniusMapper'
import { MixamoMapper } from '../../retarget/bone-automap/MixamoMapper'
import { TripoMapper } from '../../retarget/bone-automap/TripoMapper'
import { debug_log } from '../../lib/DebugLog'

/**
 * Identifier for a non-Mesh2Motion source skeleton. Extend this union when adding
 * new adapters (e.g. CMU motion-capture, Unity Humanoid).
 */
export type ForeignSkeletonOrigin = 'quaternius' | 'mixamo' | 'tripo'

interface ForeignSkeletonAdapterEntry {
  origin: ForeignSkeletonOrigin
  /** Returns true if the given set of track bone names looks like this foreign skeleton. */
  detect: (track_bone_names: Set<string>) => boolean
  /** Returns the source-bone → Mesh2Motion-bone rename map. */
  get_map: () => Record<string, string>
}

// Order matters: more-specific detectors first.
//   - Mixamo's `mixamorig` prefix is the strongest signal.
//   - Tripo's L_/R_ prefix + Hip/Pelvis split is also unambiguous.
//   - Quaternius/universal-humanoid is the most generic and goes last.
const ADAPTERS: ForeignSkeletonAdapterEntry[] = [
  {
    origin: 'mixamo',
    detect: (names) => MixamoMapper.is_target_valid_skeleton(Array.from(names)),
    get_map: () => MixamoMapper.get_source_to_target_map()
  },
  {
    origin: 'tripo',
    detect: (names) => TripoMapper.is_target_valid_skeleton(Array.from(names)),
    get_map: () => TripoMapper.get_source_to_target_map()
  },
  {
    origin: 'quaternius',
    detect: (names) => QuaterniusMapper.is_target_valid_skeleton(Array.from(names)),
    get_map: () => QuaterniusMapper.get_source_to_target_map()
  }
]

export interface ForeignSkeletonAdaptResult {
  /** True if a foreign skeleton was detected and its tracks renamed. */
  adapted: boolean
  /** When `adapted` is true, identifies which foreign skeleton matched. */
  origin?: ForeignSkeletonOrigin
  /** When `adapted` is true, the renamed clips. When false, the input clips unchanged. */
  clips: AnimationClip[]
  /** Bone names from the input that the adapter could not map (informational). */
  unmapped_bones: string[]
}

/**
 * Detects whether the given clips were authored against a known foreign skeleton
 * (Quaternius, etc.) and, if so, returns a copy with track names rewritten to use
 * Mesh2Motion bone names. Idempotent and side-effect free: input clips are not mutated.
 *
 * Run this BEFORE `AnimationUtility.clean_track_data` — clean_track_data filters
 * tracks by Mesh2Motion bone names, so foreign tracks would be incorrectly stripped
 * if rename happens later.
 */
export class ForeignSkeletonAdapter {
  static try_detect_and_rename (clips: AnimationClip[]): ForeignSkeletonAdaptResult {
    const track_bone_names = TrackBoneRenamer.collect_track_bone_names(clips)

    for (const adapter of ADAPTERS) {
      if (!adapter.detect(track_bone_names)) continue

      const map = adapter.get_map()
      const unmapped = new Set<string>()
      // Drop unmapped tracks (e.g. Tripo's `*Twist01/02` helpers, structural Pelvis,
      // any vendor-specific bones we don't have an M2M target for). Keeping them
      // would cause downstream skeleton-validation to reject the entire clip,
      // because the validator sees foreign bone names that don't exist on the
      // M2M skeleton. The unmapped bone names are still reported via the callback
      // so callers can log/surface them.
      const renamed = TrackBoneRenamer.rename_clips(clips, map, {
        dropUnmapped: true,
        onUnmapped: (name) => unmapped.add(name)
      })

      const mapped_count = track_bone_names.size - unmapped.size
      debug_log(`ForeignSkeletonAdapter: detected ${adapter.origin}; mapped ${mapped_count}/${track_bone_names.size} bones, dropped ${unmapped.size} unmapped tracks (${Array.from(unmapped).slice(0, 5).join(', ')}${unmapped.size > 5 ? ', ...' : ''}).`)
      return {
        adapted: true,
        origin: adapter.origin,
        clips: renamed,
        unmapped_bones: Array.from(unmapped).sort()
      }
    }

    return { adapted: false, clips, unmapped_bones: [] }
  }
}
